import { useEffect } from 'react';

/**
 * 手指在面板上往下（或往右）滑就關閉，像 iOS 的 sheet。
 *
 *  · `down`：只有內容已經捲到最頂（scrollTop ≤ 0）時，往下拉才會拖動面板，否則照常捲動內容。
 *  · `right`：右側抽屜用；起點在橫向可捲動區塊（表格、膠囊列）裡時不攔截。
 *  · 拖超過 90px，或快速甩（> 0.6px/ms 且 > 24px）就關閉；沒拖夠彈回去。
 *  · 用 touch 事件並以 non-passive 註冊：拖動期間要 preventDefault 才不會同時捲頁面。
 *    滑鼠不處理（桌面用 Esc／背景／關閉鈕）。
 */
const DISMISS_DISTANCE = 90;
const DISMISS_VELOCITY = 0.6;

export function useSwipeDismiss(
  panel: HTMLElement | null,
  onDismiss: () => void,
  enabled: boolean,
  direction: 'down' | 'right' = 'down',
): void {
  useEffect(() => {
    if (panel === null || !enabled) return;

    let startX = 0;
    let startY = 0;
    let startT = 0;
    let delta = 0;
    let tracking: 'idle' | 'maybe' | 'drag' | 'scroll' = 'idle';

    const scroller = (target: EventTarget | null): HTMLElement | null => {
      let el = target instanceof HTMLElement ? target : null;
      while (el !== null && el !== panel) {
        const style = getComputedStyle(el);
        if (direction === 'down' && /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) return el;
        if (direction === 'right' && /(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth) return el;
        el = el.parentElement;
      }
      return direction === 'down' && panel.scrollHeight > panel.clientHeight ? panel : null;
    };

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch === undefined || event.touches.length !== 1) return;
      startX = touch.clientX;
      startY = touch.clientY;
      startT = event.timeStamp;
      delta = 0;
      const sc = scroller(event.target);
      // 內容還沒捲到頂（down）／點在橫向可捲區（right）：交給瀏覽器。
      if (direction === 'down' && sc !== null && sc.scrollTop > 0) tracking = 'scroll';
      else if (direction === 'right' && sc !== null) tracking = 'scroll';
      else tracking = 'maybe';
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch === undefined || tracking === 'idle' || tracking === 'scroll') return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const along = direction === 'down' ? dy : dx;
      const across = direction === 'down' ? dx : dy;
      if (tracking === 'maybe') {
        if (Math.abs(along) < 6 && Math.abs(across) < 6) return;
        // 方向不對（往上、或橫向多於縱向）就放棄這一次手勢。
        if (along <= 0 || Math.abs(across) > Math.abs(along)) {
          tracking = 'scroll';
          return;
        }
        tracking = 'drag';
        panel.style.transition = 'none';
      }
      delta = Math.max(0, along);
      if (event.cancelable) event.preventDefault();
      panel.style.transform = direction === 'down' ? `translateY(${String(delta)}px)` : `translateX(${String(delta)}px)`;
    };

    const onEnd = (event: TouchEvent) => {
      const was = tracking;
      tracking = 'idle';
      if (was !== 'drag') return;
      const velocity = delta / Math.max(1, event.timeStamp - startT);
      if (delta > DISMISS_DISTANCE || (delta > 24 && velocity > DISMISS_VELOCITY)) {
        onDismiss();
        return;
      }
      panel.style.transition = 'transform 180ms cubic-bezier(0, 0, 0.2, 1)';
      panel.style.transform = '';
    };

    panel.addEventListener('touchstart', onStart, { passive: true });
    panel.addEventListener('touchmove', onMove, { passive: false });
    panel.addEventListener('touchend', onEnd);
    panel.addEventListener('touchcancel', onEnd);
    return () => {
      panel.removeEventListener('touchstart', onStart);
      panel.removeEventListener('touchmove', onMove);
      panel.removeEventListener('touchend', onEnd);
      panel.removeEventListener('touchcancel', onEnd);
    };
  }, [panel, onDismiss, enabled, direction]);
}
